import React, { createContext, useCallback, useContext, useState } from 'react';
import { Button } from './Button';

export type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
};

type ConfirmContextType = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmContextType | undefined>(undefined);

export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({});
  const [resolver, setResolver] = useState<(v: boolean) => void>(() => () => {});

  const confirm = useCallback<ConfirmContextType>((opts) => {
    setOptions(opts);
    setVisible(true);
    return new Promise<boolean>(resolve => {
      setResolver(() => resolve);
    });
  }, []);

  const onClose = (v: boolean) => {
    setVisible(false);
    resolver(v);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {visible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => onClose(false)} />
          <div className="relative z-10 w-full max-w-md rounded-md border bg-card p-4 shadow-lg">
            <div className="text-lg font-semibold">{options.title || 'Please confirm'}</div>
            {options.message && <div className="mt-2 text-sm text-muted-foreground">{options.message}</div>}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => onClose(false)}>
                {options.cancelText || 'Cancel'}
              </Button>
              <Button
                variant={options.variant === 'destructive' ? 'destructive' : 'default'}
                onClick={() => onClose(true)}
              >
                {options.confirmText || 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within ConfirmProvider');
  return ctx;
};
